// Global error handler for client-side crash logging
// Captures unhandled errors, promise rejections, and console errors
// Includes transient error filtering for edge function errors (3-consecutive-failures threshold)

import { supabase } from '@/integrations/supabase/client';
import { sendLogWithKeepalive } from '@/lib/keepaliveLogger';
import { getTraceBreadcrumbs, isTraceActive, getTraceSessionId } from '@/hooks/useSuperAdminTracer';
import { RETRYABLE_PATTERNS } from '@/lib/transientErrorFilter';
import { serializeError } from '@/lib/serializeError';
import { captureError } from '@/lib/sentry';

// Track transient errors to avoid logging them as crashes until threshold
const transientErrorCounts = new Map<string, { count: number; lastSeen: Date }>();
const TRANSIENT_ERROR_THRESHOLD = 3;
const TRANSIENT_ERROR_DECAY_MS = 60000; // 1 minute

function isTransientEdgeFunctionError(message: string): boolean {
  return RETRYABLE_PATTERNS.test(message);
}

function shouldLogTransientError(errorKey: string): boolean {
  const now = new Date();
  const tracker = transientErrorCounts.get(errorKey);
  
  if (!tracker) {
    transientErrorCounts.set(errorKey, { count: 1, lastSeen: now });
    return false; // Don't log first occurrence
  }
  
  // Reset if enough time has passed
  if (now.getTime() - tracker.lastSeen.getTime() > TRANSIENT_ERROR_DECAY_MS) {
    transientErrorCounts.set(errorKey, { count: 1, lastSeen: now });
    return false;
  }
  
  // Increment count
  tracker.count++;
  tracker.lastSeen = now;
  
  // Only log if threshold reached
  return tracker.count >= TRANSIENT_ERROR_THRESHOLD;
}

interface DeviceInfo {
  isMobile: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  userAgent: string;
  platform: string;
  screenSize: string;
  touchPoints: number;
}

function getDeviceInfo(): DeviceInfo {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/.test(ua);
  const isMobile = isIOS || isAndroid || /Mobile|webOS|BlackBerry|Opera Mini|IEMobile/.test(ua);
  
  return {
    isMobile,
    isIOS,
    isAndroid,
    userAgent: ua.substring(0, 500),
    platform: navigator.platform || 'unknown',
    screenSize: `${window.innerWidth}x${window.innerHeight}`,
    touchPoints: navigator.maxTouchPoints || 0,
  };
}

// Use CORS-safe fetch with keepalive for reliable crash logging
function logCrashToBackend(
  errorMessage: string,
  errorStack: string | undefined,
  source: string,
  metadata: Record<string, unknown> = {}
): void {
  const deviceInfo = getDeviceInfo();
  
  const payload = {
    error_message: `[${deviceInfo.isMobile ? 'MOBILE' : 'DESKTOP'}${deviceInfo.isIOS ? '/iOS' : deviceInfo.isAndroid ? '/Android' : ''}] ${errorMessage}`,
    error_stack: errorStack,
    component_stack: null,
    url: window.location.href,
    user_agent: deviceInfo.userAgent,
    user_id: null, // Can't async get user during crash
    metadata: {
      ...metadata,
      device: deviceInfo,
      source,
      timestamp: new Date().toISOString(),
      ...(isTraceActive() ? {
        trace_active: true,
        trace_session_id: getTraceSessionId(),
        trace_breadcrumbs: getTraceBreadcrumbs().slice(-50),
      } : {}),
    },
  };

  // Forward to Sentry alongside Supabase logging
  captureError(errorMessage, {
    tag_source: source,
    tag_device_type: deviceInfo.isMobile ? 'mobile' : 'desktop',
    errorStack: errorStack,
    url: window.location.href,
    ...metadata,
    device: deviceInfo,
  });

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
  
  if (supabaseUrl && supabaseKey) {
    // Use fetch with keepalive and credentials: 'omit' to avoid CORS issues
    const url = `${supabaseUrl}/functions/v1/log-client-error?apikey=${supabaseKey}`;
    
    try {
      fetch(url, {
        method: 'POST',
        body: JSON.stringify(payload),
        keepalive: true,
        credentials: 'omit', // Critical: prevents CORS wildcard + credentials conflict
        headers: {
          'Content-Type': 'application/json',
        },
      }).then(() => {
        console.log('[GlobalErrorHandler] Crash logged via keepalive fetch:', source);
      }).catch(() => {
        // Silent fail, store locally as fallback
        storeCrashLocally(payload);
      });
      return;
    } catch {
      // Fall through to localStorage
    }
  }

  storeCrashLocally(payload);
}

function storeCrashLocally(payload: Record<string, unknown>): void {
  try {
    const pendingCrashes = JSON.parse(localStorage.getItem('pendingCrashLogs') || '[]');
    pendingCrashes.push(payload);
    localStorage.setItem('pendingCrashLogs', JSON.stringify(pendingCrashes.slice(-10)));
    console.log('[GlobalErrorHandler] Crash stored in localStorage');
  } catch (e) {
    console.error('[GlobalErrorHandler] Failed to store crash:', e);
  }
}

// Send any pending crash logs from localStorage
async function sendPendingCrashLogs(): Promise<void> {
  try {
    const pendingCrashes = JSON.parse(localStorage.getItem('pendingCrashLogs') || '[]');
    if (pendingCrashes.length === 0) return;

    const { data: { user } } = await supabase.auth.getUser();
    
    for (const crash of pendingCrashes) {
      crash.user_id = user?.id || null;
      crash.metadata = crash.metadata || {};
      crash.metadata.sentFromPendingQueue = true;
      
      // Use CORS-safe fetch instead of supabase.functions.invoke
      await sendLogWithKeepalive(crash);
    }
    
    localStorage.removeItem('pendingCrashLogs');
    console.log(`[GlobalErrorHandler] Sent ${pendingCrashes.length} pending crash logs`);
  } catch (err) {
    console.error('[GlobalErrorHandler] Failed to send pending crashes:', err);
  }
}

let isInitialized = false;

export function initGlobalErrorHandler(): void {
  if (isInitialized || typeof window === 'undefined') return;
  isInitialized = true;

  // Send any pending crash logs from previous sessions
  sendPendingCrashLogs();

  // Handle uncaught errors
  window.addEventListener('error', (event) => {
    const { message, filename, lineno, colno, error } = event;
    
    logCrashToBackend(
      message || 'Unknown error',
      error?.stack || `at ${filename}:${lineno}:${colno}`,
      'window.onerror',
      {
        filename,
        lineno,
        colno,
        type: 'uncaught_error',
      }
    );
  });

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const message = serializeError(reason);
    const stack = reason instanceof Error ? reason.stack : undefined;
    
    // Check if this is a transient edge function error
    if (isTransientEdgeFunctionError(message)) {
      const errorKey = `edge-function:${message.substring(0, 100)}`;
      if (!shouldLogTransientError(errorKey)) {
        console.warn('[GlobalErrorHandler] Suppressing transient edge function error (under threshold):', message.substring(0, 100));
        return; // Don't log until threshold reached
      }
      console.warn('[GlobalErrorHandler] Threshold reached for transient error, logging:', message.substring(0, 100));
    }
    
    logCrashToBackend(
      message,
      stack,
      'unhandledrejection',
      {
        type: 'unhandled_promise_rejection',
        reason: serializeError(reason).substring(0, 1000),
      }
    );
  });

  // Catch crashes during page unload (especially on iOS)
  window.addEventListener('beforeunload', () => {
    // Check if there's a pending error state
    const lastError = (window as unknown as { __lastError?: Error }).__lastError;
    if (lastError) {
      logCrashToBackend(
        lastError.message,
        lastError.stack,
        'beforeunload',
        { type: 'crash_on_unload' }
      );
    }
  });

  // iOS-specific: catch scroll-related crashes via ResizeObserver errors
  const originalResizeObserverError = window.ResizeObserver;
  if (originalResizeObserverError) {
    window.ResizeObserver = class extends originalResizeObserverError {
      constructor(callback: ResizeObserverCallback) {
        super((entries, observer) => {
          try {
            callback(entries, observer);
          } catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            logCrashToBackend(
              error.message,
              error.stack,
              'ResizeObserver',
              { type: 'resize_observer_error' }
            );
          }
        });
      }
    };
  }

  console.log('[GlobalErrorHandler] Initialized - mobile crash logging enabled');
}

export function logManualCrash(
  message: string,
  source: string,
  metadata?: Record<string, unknown>
): void {
  logCrashToBackend(message, undefined, source, {
    type: 'manual_log',
    ...metadata,
  });
}
